import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  CloseCircleOutlined,
  CloudUploadOutlined,
  CodeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';

import Evidence from 'redpoints-model-js/model/incident/Evidence';

import {
  Col,
  Form,
  Group,
  Input,
  Notification,
  Row,
} from 'redpoints-front-components';
import {
  FileUploader,
  getLinkDataNullFormValues,
  getLinkDataValidationRules,
  LinkDataForm,
} from 'redpoints-front-common/components';
import { cast } from 'redpoints-front-common/helpers';

import { getIncidentDetailsForceValidation } from '../../../reducers/reporting/incidentDetails';
import { translate } from '../../../helpers/languageHelper';

import './incidentDetailsEvidencesTabContent.less';

const ACCEPTED_IMAGE_TYPES = 'image/jpeg, image/jpg, image/png';

const handleFileUploadError = () =>
  Notification.error({
    description: translate('form.error.file_uploading'),
  });

const getFileName = ({ uploadedFileName, evidence }) =>
  (uploadedFileName || evidence?.html || '').trim();

const getScreenShotName = ({ uploadedScreenshotName, evidence }) =>
  (uploadedScreenshotName || evidence?.screenshot || '').trim();

const evidenceCastOptions = {
  title: value => cast.string(value, null),
  html: value => cast.string(value, null),
  screenshot: value => cast.string(value, null),
};

const castEvidenceValues = ({ evidence, evidenceType, formValues }) => {
  const castedFormValues = cast(evidenceCastOptions)(formValues);

  const nullFormValues = getLinkDataNullFormValues(formValues);

  return Object.assign(
    new Evidence(),
    evidence,
    { evidenceType },
    castedFormValues,
    nullFormValues
  );
};

const IncidentDetailsEvidencesTabContent = ({
  evidence,
  evidenceType,
  isEditable,
  forceValidation,
  isMandatory,
  evidenceChange,
  evidenceValidate,
}) => {
  const [form] = Form.useForm();
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedScreenshotName, setUploadedScreenshotName] = useState('');
  const fileName = getFileName({ uploadedFileName, evidence });
  const screenshotName = getScreenShotName({
    uploadedScreenshotName,
    evidence,
  });
  const screenshotUrl = evidence?.screenshotExternalUrl ?? null;
  const htmlUrl = evidence?.htmlExternalUrl ?? null;
  const [screenshotPreview, setScreenshotPreview] = useState(screenshotUrl);

  const formFieldsConfig = {
    html: {
      isMandatory,
      value: evidence?.html,
    },
    title: {
      isMandatory: false,
      value: evidence?.title,
    },
  };

  const handleChange = formValues => {
    const newEvidenceData = castEvidenceValues({
      evidence,
      evidenceType,
      formValues,
    });
    evidenceChange(newEvidenceData);
  };

  const handleValidate = isValid => {
    evidenceValidate(isValid);
  };

  const handleClearEvidence = () => {
    const formValues = { html: null, title: null, screenshot: null };
    form.setFieldsValue(formValues);
    handleChange(formValues);
    setUploadedFileName('');
    setUploadedScreenshotName('');
    setScreenshotPreview('');
  };

  const handleRemoveScreenshot = () => {
    const formValues = { screenshot: null };
    form.setFieldsValue(formValues);
    handleChange(formValues);
    setUploadedScreenshotName('');
    setScreenshotPreview('');
  };

  const handleScreenshotUploadChange = ([{ name }]) => {
    const formValues = form.getFieldsValue();
    const newFormValues = {
      html: formValues.html,
      title: formValues.title,
      screenshot: name,
    };
    form.setFieldsValue(newFormValues);
    handleChange(newFormValues);
    setUploadedScreenshotName(name);
  };

  const handleFileUploadChange = ([{ name }]) => {
    const formValues = { html: name, title: null };
    form.setFieldsValue(formValues);
    handleChange(formValues);
    setUploadedFileName(name);
  };

  const renderEvidenceHtml = () => (
    <>
      <Col span={2}>
        <CodeOutlined className="incident-details-evidence-html-icon" />
      </Col>
      <Col span={10} className="incident-details-evidence-link-col-filename">
        {fileName}
      </Col>
      <Col span={10} className="incident-details-evidence-title">
        <Form.Item
          name="title"
          rules={getLinkDataValidationRules({
            isMandatory: formFieldsConfig.title.isMandatory,
          })}
        >
          <Input
            disabled={!isEditable}
            aria-label="incident-details-evidence-title-field"
            label={translate(
              'reporting.incidentDetails.evidence.evidenceTitleLabel'
            )}
          />
        </Form.Item>
      </Col>
      <Col span={1}>
        <Button
          className="incident-details-evidence-icon clear"
          disabled={!isEditable}
          ghost
          onClick={handleClearEvidence}
          type="text"
          aria-label="incident-details-evidence-icon-clear"
          icon={<CloseCircleOutlined />}
        />
      </Col>
      <Col span={1}>
        {!uploadedFileName && (
          <span
            className="incident-details-evidence-icon search"
            aria-label="incident-details-evidence-icon-search"
          >
            <a href={htmlUrl} rel="noopener noreferrer" target="_blank">
              <SearchOutlined disabled={!isEditable} />
            </a>
          </span>
        )}
      </Col>
    </>
  );

  const renderEmptyEvidence = () => (
    <Col span={24}>
      <FileUploader
        disabled={!isEditable}
        onError={handleFileUploadError}
        onChange={handleFileUploadChange}
      />
    </Col>
  );

  const renderScreenshot = () => (
    <>
      <Col span={22}>
        <div>{screenshotName}</div>
      </Col>
      <Col span={1}>
        <Button
          className="incident-details-evidence-icon clear"
          disabled={!isEditable}
          ghost
          onClick={handleRemoveScreenshot}
          type="text"
          aria-label="incident-details-evidence-screenshot-icon-clear"
          icon={<CloseCircleOutlined />}
        />
      </Col>
      <Col span={1}>
        {uploadedScreenshotName ? (
          <span className="incident-details-evidence-icon uploaded">
            <CloudUploadOutlined />
          </span>
        ) : (
          <span
            className="incident-details-evidence-icon search"
            aria-label="incident-details-evidence-screenshot-icon-search"
          >
            <a href={screenshotUrl} rel="noopener noreferrer" target="_blank">
              <SearchOutlined
                disabled={!isEditable}
                className="incident-details-evidence-icon-search"
                aria-label="incident-details-evidence-screenshot-icon-search"
              />
            </a>
          </span>
        )}
      </Col>
    </>
  );

  const renderEmptyScreenshot = () => (
    <Col className="image-uploader" span={24}>
      <FileUploader
        accept={ACCEPTED_IMAGE_TYPES}
        multiple={false}
        onChange={handleScreenshotUploadChange}
        maxAmountOfFiles={1}
        disabled={!isEditable || !fileName}
        onError={handleFileUploadError}
      />
    </Col>
  );

  const renderScreenshotPreview = () => (
    <Col span={24} className="incident-details-evidence-screenshot">
      <img
        alt="screenshoot"
        src={screenshotPreview}
        style={{ width: '100%' }}
      />
    </Col>
  );

  return (
    <>
      <LinkDataForm
        forceValidation={forceValidation}
        form={form}
        onChange={handleChange}
        onValidate={handleValidate}
        formFieldsConfig={formFieldsConfig}
      >
        <Group
          label={translate('reporting.incidentDetails.evidence.htmlFiles')}
        >
          <Row gutter={0} type="flex" justify="center" align="middle">
            {fileName ? renderEvidenceHtml() : renderEmptyEvidence()}
            <Form.Item
              name="html"
              rules={getLinkDataValidationRules({
                isMandatory: formFieldsConfig.html.isMandatory,
              })}
              className="incident-details-evidence-html"
            >
              <Input type="hidden" />
            </Form.Item>
          </Row>
        </Group>
        <Group
          label={translate('reporting.incidentDetails.evidence.screenshots')}
        >
          <Row gutter={0} type="flex" justify="center" align="middle">
            {screenshotName ? renderScreenshot() : renderEmptyScreenshot()}
          </Row>
          <Row gutter={0} type="flex" justify="center" align="middle">
            {screenshotPreview && renderScreenshotPreview()}
          </Row>
        </Group>
      </LinkDataForm>
    </>
  );
};

const mapStateToProps = state => ({
  forceValidation: getIncidentDetailsForceValidation(state),
});

export default connect(mapStateToProps)(IncidentDetailsEvidencesTabContent);
